import { useEffect, useState } from 'react'

import { ApiError } from './api'

/**
 * What a list knows about the record its address filters by. A filter chosen in its picker
 * comes with its name; one read from an address — a link, the browser's Back — has only an
 * id, which may name a record past any list's cap (GPT-5.6 Sol on #36) or nobody at all
 * (Copilot on #36), so the record itself is asked.
 */
export type FilterRecord =
  /** The address names nobody: the list is not narrowed by this filter. */
  | { state: 'none' }
  /** The record is known by name — chosen in the picker, or answered by the record. */
  | { state: 'named'; name: string }
  /** The record is being asked: ask the list nothing yet. */
  | { state: 'checking' }
  /** The record answered that nobody has this id: drop the filter. */
  | { state: 'gone' }
  /** The record could not be read: keep the filter, and show it as chosen. */
  | { state: 'unreadable' }

/**
 * Resolves the record an address names: by the record the picker chose, when it is that one,
 * and otherwise by the record's own endpoint. `nameOf` must keep its identity between
 * renders — a module-level function — and reject with the API's 404 for an id nobody has.
 */
export function useFilterRecord(
  id: string | undefined,
  chosen: { id: string; name: string } | null,
  nameOf: (id: string) => Promise<string>,
): FilterRecord {
  const chosenName = id !== undefined && chosen !== null && chosen.id === id ? chosen.name : null
  const [answer, setAnswer] = useState<{ id: string; record: FilterRecord } | null>(null)

  useEffect(() => {
    if (id === undefined || chosenName !== null) {
      return
    }

    let active = true

    nameOf(id)
      .then((name) => {
        if (active) {
          setAnswer({ id, record: { state: 'named', name } })
        }
      })
      .catch((caught: unknown) => {
        if (active) {
          const gone = caught instanceof ApiError && caught.status === 404
          setAnswer({ id, record: gone ? { state: 'gone' } : { state: 'unreadable' } })
        }
      })

    return () => {
      active = false
    }
  }, [id, chosenName, nameOf])

  if (id === undefined) {
    return { state: 'none' }
  }

  if (chosenName !== null) {
    return { state: 'named', name: chosenName }
  }

  // An answer about another id — the address moved on while it was asked — says nothing.
  return answer !== null && answer.id === id ? answer.record : { state: 'checking' }
}

/**
 * The record a filter's picker shows for what the address names: by name once it is known,
 * as the record "in the link" when it could not be read, and nameless while it is asked.
 */
export function filterChoice(
  id: string | undefined,
  record: FilterRecord,
  linked: string,
): { id: string; name: string } | null {
  if (id === undefined) {
    return null
  }

  switch (record.state) {
    case 'named':
      return { id, name: record.name }
    case 'unreadable':
      return { id, name: linked }
    default:
      return { id, name: '' }
  }
}
